import { type ConnectorId, type ConnectorInfo, connectorTitles } from '@hugo/core'
import { InfoIcon, PlugIcon, RefreshCwIcon, UnplugIcon } from 'lucide-react'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { GithubOptions } from '@/components/github-options'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { toast } from '@/components/ui/toast'
import { ApiError } from '@/lib/api'
import { authClient } from '@/lib/auth-client'
import {
  authorizeRequest,
  describeConnection,
  googleLinkRequest,
  oauthErrorText,
} from '@/lib/connections'
import { useConnectors, useRunTool } from '@/lib/items'
import { isNative } from '@/lib/platform'
import { useUserNow } from '@/lib/session'
import { cn } from '@/lib/utils'

type Action = 'connect' | 'sync' | 'disconnect'
type Busy = { connector: ConnectorId; action: Action } | null

/**
 * Send the user to Google to give hugo access to a connector: links the signed-in Google account
 * with the scopes the API asked for. Google sends them back to Settings with `?connect=<id>`.
 */
async function grantGoogleAccess(connector: ConnectorId, scopes: string[]): Promise<boolean> {
  const title = connectorTitles[connector]
  if (isNative) {
    // Google blocks its consent screen inside the app's WebView, and its redirect would land in
    // the browser, not in the app.
    toast.add({
      title: `Connect ${title} in a browser`,
      description: `Open hugo in a browser to give it access to ${title}.`,
      type: 'info',
    })
    return false
  }
  const callbackURL = `/settings/apps?connect=${connector}`
  const { error } = await authClient.linkSocial({
    provider: 'google',
    scopes,
    callbackURL,
    errorCallbackURL: callbackURL,
  })
  if (!error) return true
  toast.add({
    title: `Could not connect ${title}`,
    description: error.message ?? 'Google did not answer. Please try again.',
    type: 'error',
  })
  return false
}

function linkErrorText(error: string): string {
  return error === 'access_denied' ? 'Google access was not granted.' : `Google said: ${error}`
}

function ActionIcon({ busy, icon: Icon }: { busy: boolean; icon: typeof PlugIcon }) {
  return busy ? <Spinner aria-hidden="true" /> : <Icon aria-hidden="true" />
}

function ConnectorRow({
  info,
  now,
  busy,
  syncing,
  onConnect,
  onSync,
  onDisconnect,
}: {
  info: ConnectorInfo
  now: Date
  busy: Busy
  /** A sync was asked for and has not finished yet. */
  syncing: boolean
  onConnect: (connector: ConnectorId) => void
  onSync: (connector: ConnectorId) => void
  onDisconnect: (connector: ConnectorId) => void
}) {
  const view = describeConnection(info, now)
  const working = busy?.connector === info.id ? busy.action : null
  const disabled = busy !== null
  return (
    <li
      data-connector={info.id}
      data-state={view.state}
      // Phones: buttons go below the status, so the text keeps the full width.
      className="flex flex-col gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 flex-1">
        <p className={cn('font-medium', view.state === 'unavailable' && 'text-muted-foreground')}>
          {info.title}
        </p>
        <p
          className={cn(
            'text-sm break-words',
            view.state === 'error' ? 'font-medium text-destructive' : 'text-muted-foreground',
          )}
          data-testid="connection-status"
        >
          {view.text}
        </p>
        {view.error ? (
          <p className="text-sm break-words text-muted-foreground" data-testid="connection-error">
            {view.error}
          </p>
        ) : null}
      </div>
      {view.state === 'disconnected' ? (
        <Button size="sm" disabled={disabled} onClick={() => onConnect(info.id)}>
          <ActionIcon busy={working === 'connect'} icon={PlugIcon} />
          Connect
        </Button>
      ) : view.state === 'connected' || view.state === 'error' ? (
        <div className="flex flex-wrap gap-2">
          {view.state === 'error' ? (
            <Button size="sm" disabled={disabled} onClick={() => onConnect(info.id)}>
              <ActionIcon busy={working === 'connect'} icon={PlugIcon} />
              Reconnect
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={disabled || syncing}
              onClick={() => onSync(info.id)}
            >
              <ActionIcon busy={working === 'sync' || syncing} icon={RefreshCwIcon} />
              Sync now
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={() => onDisconnect(info.id)}
          >
            <ActionIcon busy={working === 'disconnect'} icon={UnplugIcon} />
            Disconnect
          </Button>
        </div>
      ) : null}
    </li>
  )
}

/**
 * Settings → Connections: one row per system hugo can collect from, with its status and
 * Connect / Sync now / Disconnect. `connectRequest` is set when Google sent the user back after
 * granting access (`/settings?connect=gmail`): it connects once, then the page drops the param.
 */
export function ConnectionsCard({
  connectRequest,
  linkError,
  oauthResult,
  onRequestHandled,
}: {
  connectRequest?: ConnectorId | undefined
  linkError?: string | undefined
  /** Back from hugo's OAuth callback (GitHub, …): connected, or why not. */
  oauthResult?: { connector: ConnectorId; error: string | null } | undefined
  onRequestHandled: () => void
}) {
  const { run } = useRunTool()
  const now = useUserNow(30_000)
  const [busy, setBusy] = useState<Busy>(null)
  const [confirming, setConfirming] = useState<{ connector: ConnectorId; open: boolean } | null>(
    null,
  )
  /**
   * A sync that finds nothing new sends no live event, so after Connect or Sync now the card asks
   * every second until the sync finished (at most 20 s). Otherwise once a minute, so "synced 3
   * minutes ago" stays true while Settings is open.
   */
  const [awaiting, setAwaiting] = useState<{ connector: ConnectorId; since: string | null } | null>(
    null,
  )
  const connectors = useConnectors(awaiting ? 1_000 : 60_000)
  useEffect(() => {
    if (!awaiting) return
    const timer = setTimeout(() => setAwaiting(null), 20_000)
    return () => clearTimeout(timer)
  }, [awaiting])
  useEffect(() => {
    if (!awaiting) return
    const info = connectors.data?.connectors.find((c) => c.id === awaiting.connector)
    const connection = info?.connection
    if (!connection) return
    const synced = connection.lastSyncAt !== null && connection.lastSyncAt !== awaiting.since
    if (synced || connection.status !== 'active') setAwaiting(null)
  }, [connectors.data, awaiting])
  const lastSyncOf = useCallback(
    (connector: ConnectorId) =>
      connectors.data?.connectors.find((c) => c.id === connector)?.connection?.lastSyncAt ?? null,
    [connectors.data],
  )

  /** `mayGrant`: on "needs access", send the user to Google (false right after coming back). */
  const connect = useCallback(
    async (connector: ConnectorId, mayGrant: boolean) => {
      const title = connectorTitles[connector]
      const grant = { scopes: null as string[] | null, authorize: null as string | null }
      const since = lastSyncOf(connector)
      setBusy({ connector, action: 'connect' })
      const result = await run(
        'connection.connect',
        { connector },
        {
          done: `${title} connected`,
          failed: `Could not connect ${title}`,
          onError: (error) => {
            const authorize =
              error instanceof ApiError ? authorizeRequest(error.code, error.details) : null
            if (authorize) {
              grant.authorize = authorize.url
              return true
            }
            const link =
              error instanceof ApiError ? googleLinkRequest(error.code, error.details) : null
            if (!link) return false
            if (mayGrant) {
              grant.scopes = link.scopes
              return true
            }
            toast.add({
              title: `Could not connect ${title}`,
              description: `hugo has no access to ${title} yet. Allow it when Google asks, then connect again.`,
              type: 'error',
            })
            return true
          },
        },
      )
      // On success the browser is on its way to Google: keep the button busy until it leaves.
      if (grant.scopes && (await grantGoogleAccess(connector, grant.scopes))) return
      // GitHub and other OAuth connectors: the provider asks the user in this browser window.
      if (grant.authorize) {
        if (isNative) {
          toast.add({
            title: `Connect ${title} in a browser`,
            description: `Open hugo in a browser to give it access to ${title}.`,
            type: 'info',
          })
        } else {
          window.location.assign(grant.authorize)
          return
        }
      }
      if (result) setAwaiting({ connector, since })
      setBusy(null)
    },
    [run, lastSyncOf],
  )

  const sync = async (connector: ConnectorId) => {
    const title = connectorTitles[connector]
    const since = lastSyncOf(connector)
    setBusy({ connector, action: 'sync' })
    const result = await run(
      'connection.sync',
      { connector },
      // The check mark means "queued"; the result shows in the row when the sync is done.
      { done: `${title} sync started`, failed: `Could not sync ${title}` },
    )
    if (result) setAwaiting({ connector, since })
    setBusy(null)
  }

  const disconnect = async (connector: ConnectorId) => {
    const title = connectorTitles[connector]
    setConfirming({ connector, open: false })
    setBusy({ connector, action: 'disconnect' })
    await run(
      'connection.disconnect',
      { connector },
      { done: `${title} disconnected`, failed: `Could not disconnect ${title}` },
    )
    setBusy(null)
  }

  // Back from Google: connect once (no second trip to Google), then drop the query params.
  const handled = useRef<string | null>(null)
  useEffect(() => {
    if (!connectRequest) {
      handled.current = null
      return
    }
    const key = `${connectRequest}|${linkError ?? ''}`
    if (handled.current === key) return
    handled.current = key
    onRequestHandled()
    if (linkError) {
      toast.add({
        title: `Could not connect ${connectorTitles[connectRequest]}`,
        description: linkErrorText(linkError),
        type: 'error',
      })
      return
    }
    void connect(connectRequest, false)
  }, [connectRequest, linkError, onRequestHandled, connect])

  // Back from an OAuth provider: say how it went once, then drop the query params.
  const oauthHandled = useRef<string | null>(null)
  useEffect(() => {
    if (!oauthResult) {
      oauthHandled.current = null
      return
    }
    const key = `${oauthResult.connector}|${oauthResult.error ?? ''}`
    if (oauthHandled.current === key) return
    oauthHandled.current = key
    onRequestHandled()
    const title = connectorTitles[oauthResult.connector]
    if (oauthResult.error) {
      toast.add({
        title: `Could not connect ${title}`,
        description: oauthErrorText(title, oauthResult.error),
        type: 'error',
      })
      return
    }
    toast.add({ title: `${title} connected`, type: 'success' })
    setAwaiting({ connector: oauthResult.connector, since: null })
  }, [oauthResult, onRequestHandled])

  const confirmTitle = confirming ? connectorTitles[confirming.connector] : ''
  return (
    <Card data-testid="connections">
      <CardHeader>
        <CardTitle>Connections</CardTitle>
        <CardDescription>
          hugo collects requests and promises from these apps. Anything it would send waits in the
          Outbox for your approval.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {connectors.data ? (
          <ul className="divide-y" aria-label="Connections">
            {connectors.data.connectors.map((info) => (
              <Fragment key={info.id}>
                <ConnectorRow
                  info={info}
                  now={now}
                  busy={busy}
                  syncing={awaiting?.connector === info.id}
                  onConnect={(id) => void connect(id, true)}
                  onSync={(id) => void sync(id)}
                  onDisconnect={(id) => setConfirming({ connector: id, open: true })}
                />
                {info.id === 'github' && info.connection?.status === 'active' ? (
                  <li className="pb-3">
                    <GithubOptions
                      connection={info.connection}
                      onReconnect={() => void connect('github', true)}
                    />
                  </li>
                ) : null}
              </Fragment>
            ))}
          </ul>
        ) : connectors.isError ? (
          <p className="text-sm text-destructive">
            Could not load connections.{' '}
            {connectors.error instanceof ApiError ? connectors.error.message : ''}
          </p>
        ) : (
          <Skeleton className="h-28 w-full" />
        )}
        {connectors.data?.writesEnabled === false ? (
          <p
            className="flex items-start gap-2 text-sm text-muted-foreground"
            data-testid="writes-off-note"
          >
            <InfoIcon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            Sending is off on this server: approved messages are recorded in the Outbox and nothing
            is sent.
          </p>
        ) : null}
      </CardContent>
      <AlertDialog
        open={confirming?.open ?? false}
        onOpenChange={(open) => {
          if (!open) setConfirming((c) => (c ? { ...c, open: false } : c))
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {confirmTitle}?</AlertDialogTitle>
            <AlertDialogDescription>
              hugo stops syncing {confirmTitle}. Items it already collected stay.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (confirming) void disconnect(confirming.connector)
              }}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
